import { useEffect, useRef } from "react";

/**
 * Custom hook to enable horizontal scrolling for a carousel using mouse wheel events.
 * @param {*} sliderRef
 * @param {*} isHovered
 * @returns
 */
function useHorizontalScrollForCarousel(sliderRef, isHovered, isMobile = false) {
  const scrollCountRef = useRef(0);
  const lastActionTimeRef = useRef(0);

  useEffect(() => {
    if (isMobile) return;
    const sliderElem = sliderRef.current?.innerSlider?.list;
    if (!sliderElem) return;

    /**
     * @param {WheelEvent} e
     * @returns
     */
    const handleWheel = (e) => {
      if (!isHovered) return;

      // Only listen to horizontal scroll (deltaX)
      const { deltaX, deltaMode } = e;
      let totalDelta = deltaX;

      // Normalize deltaMode (0 = pixels, 1 = lines, 2 = pages)
      // Convert lines to approx pixels to have consistent threshold across devices/OS
      if (deltaMode === 1) {
        totalDelta *= 16; // 1 line ~= 16px (approx)
      } else if (deltaMode === 2) {
        totalDelta *= 800; // page scroll, make it large
      }

      // Ignore tiny deltas (touchpads and precision mice can emit many small events)
      const MIN_DELTA = 20;
      if (Math.abs(totalDelta) < MIN_DELTA) return;

      e.preventDefault();

      const now = performance.now();
      const DEBOUNCE_MS = 500;

      if (now - lastActionTimeRef.current < DEBOUNCE_MS) return;

      lastActionTimeRef.current = now;

      if (totalDelta > 0) {
        sliderRef.current?.slickNext();
      } else {
        sliderRef.current?.slickPrev();
      }

      scrollCountRef.current += 1;
      setTimeout(() => {
        scrollCountRef.current = 0;
      }, DEBOUNCE_MS);
    };

    sliderElem.addEventListener("wheel", handleWheel, { passive: false });

    return () => sliderElem.removeEventListener("wheel", handleWheel);
  }, [isHovered, sliderRef, isMobile]);

  return null;
}

export default useHorizontalScrollForCarousel;
